/**
 * <ShareLinkCTA>: shareable room URL CTA.
 *
 * Per IMPLEMENTATION_PLAN §7 M3.T14. Reusable component used in:
 *   - Lobby header [1]: prominent share button (§13.8 hierarchy)
 *   - Any other context needing a copy/share CTA
 *
 * Behavior:
 *   1. Clicking "Copy link" copies the current room URL to clipboard.
 *   2. On browsers supporting Web Share API, clicking "Share" invokes the
 *      native share sheet.
 *   3. On unsupported browsers, falls back to clipboard copy only.
 *
 * The copy confirmation is transient (2s then resets).
 */

import { useState } from "preact/hooks";

type ShareLinkCTAProps = {
  roomId: string;
};

export function ShareLinkCTA({ roomId }: ShareLinkCTAProps) {
  const [copied, setCopied] = useState(false);

  const roomUrl = `${window.location.origin}/r/${roomId}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(roomUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: use execCommand for older browsers
      const el = document.createElement("textarea");
      el.value = roomUrl;
      el.style.position = "fixed";
      el.style.opacity = "0";
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join my drawlol room!",
          url: roomUrl,
        });
      } catch {
        // User cancelled share: no-op
      }
    } else {
      await handleCopy();
    }
  };

  const hasNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  return (
    <div class="share-link-cta" data-testid="share-link-cta">
      {/*
       * The 4-char room CODE is the human-readable headline (large + bold):
       * it's what people read aloud to invite a friend. The full /r/<CODE>
       * URL is demoted to a small caption, but it's still what copy/share
       * actually ship, since that's the clickable join link.
       */}
      <span class="share-link-cta__code" aria-label={`Room code: ${roomId}`}>
        {roomId}
      </span>
      <span class="share-link-url" aria-label={`Room link: ${roomUrl}`}>
        {roomUrl}
      </span>
      <div class="share-link-buttons">
        <button
          class={["share-link-copy-btn", copied && "share-link-copy-btn--copied"]
            .filter(Boolean)
            .join(" ")}
          onClick={() => void handleCopy()}
          aria-label={copied ? "Link copied!" : "Copy room link"}
          type="button"
        >
          {copied ? "✓ Copied!" : "Copy link"}
        </button>
        {hasNativeShare && (
          <button
            class="share-link-share-btn"
            onClick={() => void handleShare()}
            aria-label="Share room link"
            type="button"
          >
            Share
          </button>
        )}
      </div>
    </div>
  );
}
